import { useState } from 'react';
import { ArrowPathIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Input, Modal, ModalFooter, Spinner } from '../../../components';
import {
  useShopifyStatus,
  useConnectShopify,
  useDisconnectShopify,
  useSyncShopifyContent,
} from '../hooks/useSettings';

export default function ShopifySettings() {
  const { data: status, isLoading } = useShopifyStatus();
  const connectShopify = useConnectShopify();
  const disconnectShopify = useDisconnectShopify();
  const syncContent = useSyncShopifyContent();

  const [showForm, setShowForm] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const [shopUrl, setShopUrl] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!shopUrl.trim()) newErrors.shopUrl = 'Required';
    else if (!shopUrl.includes('.myshopify.com') && !shopUrl.includes('.'))
      newErrors.shopUrl = 'Enter a valid Shopify store URL';
    if (!apiKey.trim()) newErrors.apiKey = 'Required';
    if (!apiSecret.trim()) newErrors.apiSecret = 'Required';
    if (!accessToken.trim()) newErrors.accessToken = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    connectShopify.mutate(
      {
        shop_url: shopUrl,
        site_url: siteUrl || undefined,
        api_key: apiKey,
        api_secret: apiSecret,
        access_token: accessToken,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setApiKey('');
          setApiSecret('');
          setAccessToken('');
        },
      }
    );
  }

  function handleDisconnect() {
    disconnectShopify.mutate(undefined, {
      onSuccess: () => setShowDisconnectConfirm(false),
    });
  }

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner text="Loading Shopify status..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand/10">
              <svg className="h-6 w-6 text-brand" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.337 3.415c-.15-.082-.337-.014-.382.156l-.807 2.97c-.03.112-.142.18-.255.156a5.37 5.37 0 00-.852-.153c-2.564-.196-3.94 1.732-4.12 3.4-.023.22.146.41.366.41h1.02c.175 0 .325-.12.36-.29.21-1.007.893-1.655 1.827-1.655.217 0 .427.033.627.096l-1.83 6.732c-.044.16.076.32.24.32h1.35c.153 0 .287-.104.324-.253l2.287-8.41c.21-.77.085-1.347-.196-1.734a1.86 1.86 0 00-.16-.182l.18-.662c.042-.155-.03-.32-.179-.4z" />
                <path d="M20.498 5.003c-.453-1.267-1.788-1.868-3.34-2.102C15.603 2.665 13.51 2.5 12 2.5s-3.603.165-5.157.401c-1.553.234-2.888.835-3.341 2.102-.452 1.268-.452 3.898-.452 5.497s0 4.23.452 5.497c.453 1.268 1.788 1.869 3.341 2.102 1.554.237 3.646.401 5.157.401s3.603-.164 5.157-.401c1.553-.233 2.888-.834 3.341-2.102.452-1.267.452-3.898.452-5.497s0-4.229-.452-5.497zm-1.44 10.337c-.303.85-1.268 1.322-2.569 1.518-1.388.21-3.352.362-4.489.362s-3.1-.152-4.489-.362c-1.3-.196-2.266-.668-2.57-1.518-.35-.984-.441-3.302-.441-4.84s.09-3.856.442-4.84c.303-.85 1.269-1.322 2.569-1.518C8.9 3.932 10.863 3.78 12 3.78s3.1.152 4.489.362c1.3.196 2.266.668 2.569 1.518.352.984.442 3.302.442 4.84s-.09 3.856-.442 4.84z" />
              </svg>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-ink">Shopify Integration</h3>
              <p className="text-sm text-ink-2">
                Connect your Shopify store for order lookup and customer context
              </p>
            </div>
          </div>
          {status?.connected ? (
            <Badge variant="success" size="md">
              <CheckCircleIcon className="mr-1 h-4 w-4" />
              Connected
            </Badge>
          ) : (
            <Badge variant="danger" size="md">
              <XCircleIcon className="mr-1 h-4 w-4" />
              Not Connected
            </Badge>
          )}
        </div>

        {status?.connected && (
          <div className="mt-4 rounded-card bg-surface-2 p-4">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-xs font-medium text-ink-3">Shop URL</dt>
                <dd className="text-sm text-ink">{status.shopUrl}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-3">Website URL</dt>
                <dd className="text-sm text-ink">{status.siteUrl || 'Not set'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-3">API Key</dt>
                <dd className="text-sm text-ink">
                  {status.hasApiKey ? 'Configured' : 'Not set'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-ink-3">Access Token</dt>
                <dd className="text-sm text-ink">
                  {status.hasAccessToken ? 'Configured' : 'Not set'}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          {!status?.connected ? (
            <Button onClick={() => setShowForm(!showForm)}>
              Connect Shopify
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setShowForm(!showForm)}>
                Update Credentials
              </Button>
              <Button
                variant="outline"
                onClick={() => syncContent.mutate()}
                isLoading={syncContent.isPending}
              >
                <ArrowPathIcon className="mr-1.5 h-4 w-4" />
                Sync Content
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowDisconnectConfirm(true)}
              >
                Disconnect
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Connection Form */}
      {showForm && (
        <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
          <h3 className="mb-4 font-display text-lg font-semibold text-ink">
            Shopify Credentials
          </h3>
          <p className="mb-6 text-sm text-ink-2">
            Enter your Shopify Admin API credentials. You can find these in your{' '}
            <a
              href="https://admin.shopify.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:underline"
            >
              Shopify Admin
            </a>{' '}
            under Settings &gt; Apps and sales channels &gt; Develop apps.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Shop URL"
                value={shopUrl}
                onChange={(e) => setShopUrl(e.target.value)}
                placeholder="your-store.myshopify.com"
                error={errors.shopUrl}
                helperText="Your Shopify store domain"
              />
              <Input
                label="Website URL"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder="https://www.yourstore.com"
                helperText="Customer-facing website (optional)"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Shopify Admin API key"
                error={errors.apiKey}
              />
              <Input
                label="API Secret"
                type="password"
                value={apiSecret}
                onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Shopify Admin API secret"
                error={errors.apiSecret}
              />
            </div>

            <Input
              label="Access Token"
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="Admin API access token"
              error={errors.accessToken}
            />

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" isLoading={connectShopify.isPending}>
                {status?.connected ? 'Update Connection' : 'Connect Store'}
              </Button>
              <Button variant="outline" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Disconnect Confirmation */}
      <Modal
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        title="Disconnect Shopify"
        size="sm"
      >
        <p className="text-sm text-ink-2">
          Are you sure you want to disconnect your Shopify store? Order lookup
          and customer context will no longer be available.
        </p>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setShowDisconnectConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDisconnect}
            isLoading={disconnectShopify.isPending}
          >
            Disconnect
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
